import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListActionGroup from '../../component/ListActionGroup'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import { BUTTON_LOADING_ICON, FORM_DIALOG_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import { LIST_ACTION_EDIT_BUTTON_SX } from '../../component/listActionIconStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'


export default function MedalProductOptions({ role }) {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(createInitialListState({
        location,
        getExtraState: ({ locationState, searchParams }) => ({
            initialModalMode: searchParams.get('modal') || locationState.modalMode || '',
            initialEditId: searchParams.has('optionId')
                ? parseNumberParam(searchParams.get('optionId'), null)
                : (locationState.editId || null),
            initialDraftOptionName: searchParams.has('draftOptionName')
                ? (searchParams.get('draftOptionName') || '')
                : (locationState.optionName ?? null),
            initialDraftPrice: searchParams.has('draftPrice')
                ? (searchParams.get('draftPrice') || '')
                : (locationState.price ?? null),
        }),
    }))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [saving, setSaving] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [openDialog, setOpenDialog] = useState(() => initialListState.initialModalMode === 'new' || Boolean(initialListState.initialEditId))
    const [modalMode, setModalMode] = useState(() => initialListState.initialEditId ? 'edit' : (initialListState.initialModalMode === 'new' ? 'new' : ''))
    const [editId, setEditId] = useState(initialListState.initialEditId)
    const [optionName, setOptionName] = useState(initialListState.initialDraftOptionName || '')
    const [price, setPrice] = useState(initialListState.initialDraftPrice || '')
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/medal/getProductOptionList', { page: curPage, offset: offset })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '메달 옵션 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '메달 옵션 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery: '',
            extraParams: [
                {
                    param: 'modal',
                    value: modalMode,
                    shouldInclude: () => openDialog && (modalMode === 'new' || modalMode === 'edit'),
                },
                {
                    param: 'optionId',
                    value: editId,
                    shouldInclude: () => openDialog && modalMode === 'edit' && Boolean(editId),
                },
                {
                    param: 'draftOptionName',
                    value: optionName,
                    shouldInclude: () => openDialog && (modalMode === 'new' || modalMode === 'edit'),
                },
                {
                    param: 'draftPrice',
                    value: price,
                    shouldInclude: () => openDialog && (modalMode === 'new' || modalMode === 'edit'),
                },
            ],
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, editId, history, location.pathname, location.search, location.state, modalMode, openDialog, optionName, price])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading) || saving

    // 등록/수정 관리 화면은 조회 실패와 데이터 없음만 구분해 안내한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="메달 옵션 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 메달 옵션이 없습니다."
                description="메달 옵션을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id) || saving) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data: response } = await API.post('/api/v1/admin/medal/deleteProductOption', { medalProductOptionId: id })

            if (response.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(response.message)

                if (curPage > 1 && data.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메달 옵션을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    const resetModalState = () => {
        if (saving) {
            return
        }
        setOptionName('')
        setPrice('')
        setEditId(null)
        setModalMode('')
        setOpenDialog(false)
    }

    // 수정 모달은 query 기준으로도 다시 열 수 있게 편집 대상 id를 함께 유지한다.
    const handleEdit = (item) => {
        if (isBusy) {
            return
        }
        setOptionName(item.optionName)
        setPrice(item.price)
        setEditId(item.medalProductOptionId)
        setModalMode('edit')
        setOpenDialog(true)
    }

    const handleCreate = () => {
        if (isBusy) {
            return
        }

        setOptionName('')
        setPrice('')
        setEditId(null)
        setModalMode('new')
        setOpenDialog(true)
    }

    // 수정 모달은 새로고침 후에도 draft 값이 있으면 그 값을 우선 복원한다.
    useEffect(() => {
        if (!openDialog || modalMode !== 'edit' || !editId) {
            return
        }

        const targetItem = data.find((item) => Number(item.medalProductOptionId) === Number(editId))

        if (!targetItem) {
            setModalMode('')
            setEditId(null)
            setOpenDialog(false)
            return
        }

        setOptionName(initialListState.initialDraftOptionName ?? targetItem.optionName)
        setPrice(initialListState.initialDraftPrice ?? String(targetItem.price ?? ''))
    }, [data, editId, initialListState.initialDraftOptionName, initialListState.initialDraftPrice, modalMode, openDialog])

    const handleSubmit = async () => {
        if (saving || actionLoading) {
            return
        }

        const nextOptionName = (optionName || '').trim()

        if (!nextOptionName || !price) {
            setMessageSnackbar("옵션명, 가격을 입력해주세요.")
            setSeveritySnackbar("warning")
            setOpenSnackbar(true)
            return
        }

        try {
            setSaving(true)
            let url = 'createProductOption'

            if (editId) url = 'updateProductOption'

            const { data } = await API.post(`/api/v1/admin/medal/${url}`, { medalProductOptionId: editId, optionName: nextOptionName, price })

            if (data.code === 200) {
                setOptionName('')
                setPrice('')
                setEditId(null)
                setModalMode('')
                setOpenDialog(false)
                setOpenSnackbar(true)
                setSeveritySnackbar("success")
                setMessageSnackbar(url === "createProductOption" ? "등록되었습니다." : "수정되었습니다.")
                await getData(false)
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar("error")
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            console.error(err)
            setOpenSnackbar(true)
            setSeveritySnackbar("error")
            setMessageSnackbar("등록 중 오류가 발생했습니다.")
        } finally {
            setSaving(false)
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>메달 옵션 리스트</Title>
                    <ListTableToolbar
                        right={(
                            <Button
                                onClick={handleCreate}
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                신규추가
                            </Button>
                        )}
                    />
                    {/* 테이블 */}
                    <ListTableContainer sx={{ opacity: isBusy ? 0.6 : 1, pointerEvents: isBusy ? 'none' : 'auto' }}>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>옵션명</TableCell>
                                <TableCell>가격</TableCell>
                                {/* <TableCell>등록일</TableCell> */}
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((row) => (
                                    <TableRow key={row.medalProductOptionId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.optionName}</TableCell>
                                        <TableCell>{formatNumber(row.price)}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup>
                                                <Tooltip title="Edit">
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        onClick={() => handleEdit(row)}
                                                        aria-label="Edit"
                                                        disabled={isBusy}
                                                        sx={{
                                                            ...LIST_ACTION_EDIT_BUTTON_SX,
                                                            cursor: isBusy ? 'default' : 'pointer',
                                                            opacity: isBusy ? 0.5 : 1,
                                                            color: isBusy ? 'rgba(100, 116, 139, 0.42)' : LIST_ACTION_EDIT_BUTTON_SX.color,
                                                        }}
                                                    >
                                                        <ModeEditIcon />
                                                    </Box>
                                                </Tooltip>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.medalProductOptionId} disabled={isBusy}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}

                    <Dialog open={openDialog} onClose={saving ? undefined : resetModalState} maxWidth="sm" fullWidth>
                        <DialogTitle>메달 옵션 신규 {editId ? '수정' : '등록'}</DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                                <TextField
                                    label="옵션명"
                                    variant="outlined"
                                    type="text"
                                    value={optionName}
                                    onChange={(e) => {
                                        setOptionName(e.target.value)
                                    }}
                                    disabled={saving}
                                    fullWidth
                                />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                                <TextField
                                    label="가격"
                                    variant="outlined"
                                    type="text"
                                    value={price}
                                    onChange={(e) => {
                                        let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                        if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                        setPrice(onlyNums)
                                    }}
                                    disabled={saving}
                                    fullWidth
                                />
                            </Box>
                            
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => resetModalState()} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={saving}>취소</Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                                sx={FORM_DIALOG_ACTION_BUTTON_SX}
                                disabled={saving}
                                startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                aria-busy={saving}
                            >
                                {saving ? '저장 중...' : '저장'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            )}
        </Grid>
    )
}
